import { useRef } from 'react';
import type { Project } from '@hangar/shared';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { focusElement } from '@/lib/useFormState';
import { DefaultEnvironmentsOption, DescriptionField, NameField, ProductionBranchField, SlugField } from './NewProjectFields';
import { useNewProjectForm } from './useNewProjectForm';

interface FormProps {
  onDone: (project: Project) => void;
  onCancel: () => void;
}

/** The form inside the dialog: slug with live validation, required marked before submit, per-field API errors. */
function NewProjectForm({ onDone, onCancel }: FormProps) {
  const slugRef = useRef<HTMLInputElement>(null);
  const f = useNewProjectForm({ onDone, slugRef });
  return (
    <form data-testid="project-form" id="project-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex min-h-0 flex-col" aria-label="New project">
      <DialogHeader>
        <DialogTitle>New project</DialogTitle>
        <DialogDescription id="new-project-desc">A project groups the apps of one product with its environments.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <NameField f={f} />
          <SlugField f={f} slugRef={slugRef} />
          <DescriptionField f={f} />
        </div>
        <DefaultEnvironmentsOption f={f} />
        <ProductionBranchField f={f} />
        <FormErrorAlert message={f.formError} />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" data-testid="project-submit" loading={f.pending}>
          Create project
        </Button>
      </DialogFooter>
    </form>
  );
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The project just created (the dialog has closed). */
  onCreated?: (project: Project) => void;
}

/**
 * "New project" as a dialog, like Create client and Edit project: one decision of a handful of fields. It opens on the
 * name, which fills the slug as it is typed; a fresh form every time it opens.
 */
export function NewProjectDialog({ open, onOpenChange, onCreated }: DialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-project-dialog"
        aria-describedby="new-project-desc"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          focusElement('project-name');
        }}
      >
        {open ? (
          <NewProjectForm
            onCancel={() => onOpenChange(false)}
            onDone={(project) => {
              onOpenChange(false);
              onCreated?.(project);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
